import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Award, Sparkles, Youtube } from 'lucide-react';

const About = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const credentials = [
    { icon: MapPin, text: 'Based in London' },
    { icon: Award, text: 'Masters in Digital Marketing' },
    { icon: Sparkles, text: 'Social Media Influencer' },
    { icon: Youtube, text: 'Content Creator & YouTuber' },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      ref={sectionRef}
      className="py-24 sm:py-32 px-6 sm:px-8 lg:px-12 bg-[#F3F1ED]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
              <img
                src="https://customer-assets.emergentagent.com/job_hyperlab-marketing/artifacts/b0738gas_88877721-2420-434A-8D17-C2DA4AD54FAF.JPG"
                alt="Rutvi Ahir - Founder of HypeLab Marketing"
                className="w-full h-full object-cover"
                data-testid="about-image"
              />
              {/* Decorative overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/20 to-transparent" />
            </div>

            {/* Floating Card */}
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 20, scale: 0.9 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-xl border border-[#1C1917]/5"
            >
              <p className="text-4xl font-black text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>5+</p>
              <p className="text-sm text-[#595959] font-medium">Years Experience</p>
            </motion.div>

            {/* Decorative Element */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#E8E6F8] rounded-full blur-3xl opacity-60" />
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#AA94FF] mb-4">
              Meet The Founder
            </p>
            
            <h2
              className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#1C1917] mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Hi, I'm Rutvi Ahir
            </h2>

            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#78716C] mb-6">
              As a London-based social media influencer, content creator, and YouTuber with a 
              Master's degree in Digital Marketing, I've spent years mastering the art of 
              digital storytelling and brand growth.
            </p>

            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#595959] mb-8">
              I founded HypeLab Marketing to help businesses thrive in the ever-evolving 
              digital landscape. My hands-on experience as a creator, combined with my 
              academic expertise, gives me a unique perspective on what truly works in 
              today's market.
            </p>

            {/* Credentials */}
            <div className="grid grid-cols-2 gap-4 mb-10">
              {credentials.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  className="flex items-center gap-3"
                  data-testid={`credential-${index}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#E8E6F8] flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-[#1C1917]" />
                  </div>
                  <span className="text-sm font-semibold text-[#1C1917]">{item.text}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="about-cta-button"
              className="group bg-[#AA94FF] text-[#F4F4F3] rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wider hover:scale-105 hover:shadow-lg hover:bg-[#CC5003] transition-all duration-300 flex items-center gap-3"
            >
              Let's Work Together
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
